import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Download, Loader2, Package, Sparkles, Calendar, ChevronDown, ChevronUp, Lock } from 'lucide-react';
import { ModUpdate } from '@/lib/types';
import { getAllModUpdates } from '@/lib/actions';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/firebase/config';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { RichText } from '@/components/ui/rich-text';
import { useToast } from '@/hooks/use-toast';

const getModDownloadFn = httpsCallable(functions, 'getModDownload');

function toDateSafe(v: any): Date {
    if (!v) return new Date(0);
    if (typeof v === 'object' && typeof v.toDate === 'function') return v.toDate();
    const d = new Date(v);
    return isNaN(d.getTime()) ? new Date(0) : d;
}

function hexToRgba(hex: string, alpha: number) {
    const h = (hex || '#10b981').replace('#', '');
    const r = parseInt(h.substring(0, 2), 16);
    const g = parseInt(h.substring(2, 4), 16);
    const b = parseInt(h.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export default function ModCofradePage() {
    const { toast } = useToast();
    const [updates, setUpdates] = useState<ModUpdate[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [expanded, setExpanded] = useState<string | null>(null);
    const [downloadingId, setDownloadingId] = useState<string | null>(null);

    useEffect(() => {
        async function fetchUpdates() {
            try {
                const { updates, error } = await getAllModUpdates();
                if (error) {
                    setError(error);
                } else {
                    setUpdates(updates);
                    if (updates.length > 0) setExpanded(updates[0].id);
                }
            } catch {
                setError('Error inesperado al cargar las actualizaciones.');
            } finally {
                setIsLoading(false);
            }
        }
        fetchUpdates();
    }, []);

    async function handleDownload(update: ModUpdate) {
        setDownloadingId(update.id);
        try {
            const res: any = await getModDownloadFn({ id: update.id });
            const url = res?.data?.url;
            if (url) {
                window.open(url, '_blank', 'noopener,noreferrer');
            } else {
                toast({ title: 'No disponible', description: 'No se pudo obtener la descarga.', variant: 'destructive' });
            }
        } catch (e: any) {
            const msg = e?.message?.includes('bloqueada')
                ? 'La descarga de esta versión está bloqueada.'
                : (e?.message || 'No se pudo descargar.');
            toast({ title: 'Descarga no disponible', description: msg, variant: 'destructive' });
        } finally {
            setDownloadingId(null);
        }
    }

    return (
        <div className="space-y-8">
            {/* Hero header */}
            <div className="relative overflow-hidden rounded-3xl border border-emerald-500/20 bg-gradient-to-br from-emerald-500/15 via-emerald-500/5 to-transparent p-8">
                <div className="absolute -top-10 -right-10 h-52 w-52 rounded-full bg-emerald-500/10 blur-3xl" />
                <div className="absolute -bottom-8 left-10 h-36 w-36 rounded-full bg-emerald-400/8 blur-2xl" />
                <div
                    className="pointer-events-none absolute inset-0 opacity-[0.03]"
                    style={{
                        backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 20px, currentColor 20px, currentColor 21px), repeating-linear-gradient(90deg, transparent, transparent 20px, currentColor 20px, currentColor 21px)',
                    }}
                />
                <div className="relative flex items-center gap-5">
                    <div className="flex h-16 w-16 items-center justify-center rounded-2xl border border-emerald-500/30 bg-emerald-500/15 shadow-lg shadow-emerald-500/10">
                        <Package className="h-8 w-8 text-emerald-400" />
                    </div>
                    <div>
                        <h1 className="font-headline text-4xl font-bold tracking-tight">Mod Cofrade</h1>
                        <p className="mt-1 text-muted-foreground">
                            Actualizaciones y descargas del mod oficial de Jartiland
                        </p>
                    </div>
                    {updates.length > 0 && (
                        <div className="ml-auto hidden sm:flex items-center gap-2 rounded-2xl border border-emerald-500/20 bg-emerald-500/10 px-4 py-2">
                            <Sparkles className="h-4 w-4 text-emerald-400" />
                            <span className="text-sm font-semibold text-emerald-400">{updates.length} versiones</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Content */}
            {isLoading ? (
                <div className="flex h-52 items-center justify-center">
                    <Loader2 className="h-8 w-8 animate-spin text-emerald-400" />
                </div>
            ) : error ? (
                <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-destructive/20 bg-destructive/5 py-16">
                    <p className="text-destructive">{error}</p>
                </div>
            ) : updates.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-white/8 py-20 text-muted-foreground">
                    <Package className="h-12 w-12 opacity-30" />
                    <p className="text-lg font-medium">No hay actualizaciones disponibles</p>
                </div>
            ) : (
                <div className="relative">
                    <div className="absolute left-6 top-8 bottom-8 hidden w-px bg-gradient-to-b from-emerald-500/40 via-emerald-500/15 to-transparent sm:block" />

                    <div className="space-y-4">
                        <AnimatePresence>
                            {updates.map((update, idx) => {
                                const isOpen = expanded === update.id;
                                const isLatest = idx === 0;
                                const date = toDateSafe(update.date);
                                const accent = update.accentColor || '#10b981';
                                const featured = update.featured ?? isLatest;
                                const locked = update.locked ?? true;
                                const badge = update.badgeText || (isLatest ? 'Última versión' : '');

                                return (
                                    <motion.div
                                        key={update.id}
                                        initial={{ opacity: 0, x: -12 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: idx * 0.06, type: 'spring', stiffness: 240, damping: 22 }}
                                        className="relative sm:pl-16"
                                    >
                                        {/* Timeline dot */}
                                        <div
                                            className="absolute left-3.5 top-6 hidden h-5 w-5 -translate-x-1/2 items-center justify-center rounded-full border-2 sm:flex"
                                            style={{
                                                borderColor: featured ? accent : 'rgba(255,255,255,0.2)',
                                                background: featured ? hexToRgba(accent, 0.2) : 'transparent',
                                                boxShadow: featured ? `0 0 10px ${hexToRgba(accent, 0.4)}` : undefined,
                                            }}
                                        >
                                            {featured && <div className="h-2 w-2 animate-pulse rounded-full" style={{ background: accent }} />}
                                        </div>

                                        {/* Card */}
                                        <div
                                            className="overflow-hidden rounded-2xl border transition-all duration-300"
                                            style={{
                                                borderColor: featured ? hexToRgba(accent, 0.35) : 'rgba(255,255,255,0.08)',
                                                background: featured
                                                    ? `linear-gradient(135deg, ${hexToRgba(accent, 0.12)}, transparent)`
                                                    : 'rgba(255,255,255,0.02)',
                                                boxShadow: featured ? `0 4px 24px ${hexToRgba(accent, 0.10)}` : undefined,
                                            }}
                                        >
                                            <button className="w-full text-left" onClick={() => setExpanded(isOpen ? null : update.id)}>
                                                <div className="flex items-center gap-4 px-5 py-4">
                                                    <div
                                                        className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl border font-mono text-xs font-bold"
                                                        style={{
                                                            borderColor: hexToRgba(accent, 0.3),
                                                            background: hexToRgba(accent, 0.15),
                                                            color: accent,
                                                        }}
                                                    >
                                                        {update.version ? `v${update.version}` : `#${updates.length - idx}`}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <h3 className="font-headline text-lg font-bold leading-tight truncate">{update.title}</h3>
                                                            {badge && (
                                                                <span
                                                                    className="flex items-center gap-1 rounded-full px-2 py-0.5 text-[0.6rem] font-bold uppercase tracking-wider"
                                                                    style={{ background: hexToRgba(accent, 0.15), color: accent }}
                                                                >
                                                                    <span className="h-1.5 w-1.5 rounded-full" style={{ background: accent }} />
                                                                    {badge}
                                                                </span>
                                                            )}
                                                            {locked && (
                                                                <span className="flex items-center gap-1 rounded-full bg-muted/60 px-2 py-0.5 text-[0.6rem] font-semibold uppercase tracking-wider text-muted-foreground">
                                                                    <Lock className="h-2.5 w-2.5" /> Próximamente
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
                                                            <Calendar className="h-3 w-3" />
                                                            {format(date, "d 'de' MMMM, yyyy", { locale: es })}
                                                        </div>
                                                    </div>
                                                    <div className="flex-shrink-0 text-muted-foreground">
                                                        {isOpen ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                                                    </div>
                                                </div>
                                            </button>

                                            <AnimatePresence>
                                                {isOpen && (
                                                    <motion.div
                                                        initial={{ height: 0, opacity: 0 }}
                                                        animate={{ height: 'auto', opacity: 1 }}
                                                        exit={{ height: 0, opacity: 0 }}
                                                        transition={{ type: 'spring', stiffness: 280, damping: 26 }}
                                                        className="overflow-hidden"
                                                    >
                                                        <div className="border-t border-white/6 px-5 pb-5 pt-4">
                                                            <RichText className="text-sm leading-relaxed text-muted-foreground">{update.description}</RichText>

                                                            {(update.hasDownload || update.downloadUrl) && (
                                                                <div className="mt-4">
                                                                    {locked ? (
                                                                        <Button disabled variant="outline"
                                                                            className="gap-2 cursor-not-allowed border-white/10 text-muted-foreground">
                                                                            <Lock className="h-4 w-4" />
                                                                            Descarga bloqueada
                                                                        </Button>
                                                                    ) : (
                                                                        <Button
                                                                            onClick={() => handleDownload(update)}
                                                                            disabled={downloadingId === update.id}
                                                                            className="gap-2 text-white shadow-md"
                                                                            style={{ background: accent, boxShadow: `0 4px 14px ${hexToRgba(accent, 0.25)}` }}
                                                                        >
                                                                            {downloadingId === update.id
                                                                                ? <Loader2 className="h-4 w-4 animate-spin" />
                                                                                : <Download className="h-4 w-4" />}
                                                                            Descargar
                                                                        </Button>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </AnimatePresence>
                    </div>
                </div>
            )}
        </div>
    );
}
